import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { BsFileImage, BsDownload } from 'react-icons/bs';
import { FiUploadCloud } from 'react-icons/fi';
import * as pdfjsLib from 'pdfjs-dist/webpack';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import SEO from '../components/SEO';
import { pdfToImagesSchemas } from '../schemas';
import './ToolPage.css';

const PDFToImages = () => {
  const [file, setFile] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [format, setFormat] = useState('png');
  const [quality, setQuality] = useState(1.0);
  const [scale, setScale] = useState(2.0); // Higher scale = better quality
  const [previewImages, setPreviewImages] = useState([]);
  const [pdfDocument, setPdfDocument] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      await loadPDF(selectedFile);
    }
  };

  const loadPDF = async (pdfFile) => {
    try {
      setFile(pdfFile);
      const arrayBuffer = await pdfFile.arrayBuffer();
      
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      const pageCount = pdf.numPages;
      
      setPdfDocument(pdf);
      setTotalPages(pageCount);
      
      // Generate preview thumbnails for all pages
      await generatePreviews(pdf, pageCount);
    } catch (error) {
      console.error('Error loading PDF:', error);
      alert('Error loading PDF. Please try again.');
    }
  };

  const generatePreviews = async (pdf, count) => {
    const previews = [];
    
    for (let i = 1; i <= count; i++) {
      try {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 0.5 });
        
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        
        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;
        
        const thumbnail = canvas.toDataURL('image/png');
        previews.push({
          pageNum: i,
          thumbnail: thumbnail
        });
      } catch (error) {
        console.error(`Error generating preview for page ${i}:`, error);
      }
    }
    
    setPreviewImages(previews);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      await loadPDF(droppedFile);
    }
  };

  const convertToImages = async () => {
    if (!pdfDocument) return;
    
    setIsProcessing(true);

    try {
      const zip = new JSZip();
      const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
      
      for (let i = 1; i <= totalPages; i++) {
        const page = await pdfDocument.getPage(i);
        const viewport = page.getViewport({ scale: scale });
        
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        
        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;
        
        // Convert canvas to blob
        const blob = await new Promise((resolve) => {
          canvas.toBlob(resolve, mimeType, quality);
        });
        
        // Add to zip with proper filename
        const filename = `page-${String(i).padStart(3, '0')}.${format}`;
        zip.file(filename, blob);
      }
      
      // Generate and download zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const fileName = file.name.replace('.pdf', '');
      saveAs(zipBlob, `${fileName}-images.zip`);
      
    } catch (error) {
      console.error('Error converting PDF:', error);
      alert('Error converting PDF to images. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadSinglePage = async (pageNum) => {
    if (!pdfDocument) return;
    
    try {
      const page = await pdfDocument.getPage(pageNum);
      const viewport = page.getViewport({ scale: scale });
      
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
      
      const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, mimeType, quality);
      });
      
      const filename = `page-${pageNum}.${format}`;
      saveAs(blob, filename);
      
    } catch (error) {
      console.error('Error downloading page:', error);
      alert('Error downloading page. Please try again.');
    }
  };

  return (
    <div className="tool-page">
      <SEO 
        title="PDF to Images Converter – Convert PDF to JPG/PNG"
        description="Convert PDF pages to high-quality JPG or PNG images online free. Fast PDF-to-image conversion, no software required."
        keywords="pdf to jpg, pdf to image converter, convert pdf to png"
        path="/pdf-to-images"
        schema={pdfToImagesSchemas}
      />
      <section className="tool-hero">
        <div className="tool-hero-container">
          <motion.div 
            className="tool-icon-large"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <BsFileImage />
          </motion.div>

          <motion.h1
            className="tool-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            PDF to Images
          </motion.h1>

          <motion.p
            className="tool-description"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Convert PDF pages to PNG or JPG images with custom quality
          </motion.p>
        </div>
      </section>

      <section className="tool-workspace">
        <div className="workspace-container">
          {!file ? (
            <motion.div
              className={`upload-zone ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => fileInputRef.current?.click()}
            >
              <FiUploadCloud className="upload-icon" />
              <h3>Drop PDF file here or click to browse</h3>
              <p>Convert PDF pages to high-quality images</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </motion.div>
          ) : (
            <div className="pdf-editor-layout">
              <div className="pdf-info-preview-card">
                <div className="pdf-mini-preview">
                  {previewImages.length > 0 && (
                    <img 
                      src={previewImages[0].thumbnail} 
                      alt="PDF Preview"
                      className="pdf-mini-thumbnail"
                    />
                  )}
                </div>
                <div className="pdf-info-details">
                  <h3>{file.name}</h3>
                  <p>{totalPages} pages • {(file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
              </div>

              <div className="pdf-control-panel">
                <div className="conversion-settings">
                  <h4>Output Settings</h4>
                  
                  <div className="setting-group">
                    <label>Image Format</label>
                    <div className="format-options">
                      <button
                        className={`format-btn ${format === 'png' ? 'active' : ''}`}
                        onClick={() => setFormat('png')}
                      >
                        PNG
                      </button>
                      <button
                        className={`format-btn ${format === 'jpg' ? 'active' : ''}`}
                        onClick={() => setFormat('jpg')}
                      >
                        JPG
                      </button>
                    </div>
                  </div>

                  <div className="setting-group">
                    <label>Image Quality: {Math.round(quality * 100)}%</label>
                    <input
                      type="range"
                      min="0.5"
                      max="1"
                      step="0.1"
                      value={quality}
                      onChange={(e) => setQuality(parseFloat(e.target.value))}
                      className="quality-slider"
                    />
                    <div className="quality-labels">
                      <span>Smaller Size</span>
                      <span>Best Quality</span>
                    </div>
                  </div>

                  <div className="setting-group">
                    <label>Resolution Scale: {scale}x</label>
                    <input
                      type="range"
                      min="1"
                      max="4"
                      step="0.5"
                      value={scale}
                      onChange={(e) => setScale(parseFloat(e.target.value))}
                      className="quality-slider"
                    />
                    <div className="quality-labels">
                      <span>Standard (1x)</span>
                      <span>Ultra HD (4x)</span>
                    </div>
                  </div>
                </div>

                <div className="pages-preview">
                  <h4>All Pages ({totalPages} pages)</h4>
                  <div className="preview-grid-images">
                    {previewImages.map((preview) => (
                      <motion.div 
                        key={preview.pageNum} 
                        className="preview-page-image"
                        whileHover={{ scale: 1.05 }}
                      >
                        <img 
                          src={preview.thumbnail} 
                          alt={`Page ${preview.pageNum}`}
                          className="preview-thumbnail"
                        />
                        <div className="preview-overlay">
                          <button
                            className="btn-preview-download"
                            onClick={() => downloadSinglePage(preview.pageNum)}
                          >
                            <BsDownload /> Page {preview.pageNum}
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="action-buttons">
                <motion.button
                  className="btn-primary-large"
                  onClick={convertToImages}
                  disabled={isProcessing}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <BsDownload />
                  {isProcessing ? `Converting ${totalPages} pages...` : `Download All Images in ZIP`}
                </motion.button>
                <p className="conversion-note">
                  All {totalPages} images will be packaged in a single ZIP file
                </p>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PDFToImages;
